"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";

import { signOut } from "@/core/auth/action.auth";
import authSettings from "@/shared/config/authSettings";
import {
  useAuth,
  useMeiliRefreshState,
  useRag,
  useUserChoices,
} from "@/state/globalStore";

export default function usePrivateLogout() {
  const { logout } = useAuth();
  const {
    resetSelectedSituations,
    resetSelectedThoughts,
    resetSelectedSuggestedThoughts,
    resetSelectedOrigins,
  } = useUserChoices();
  const { incrementMeiliRefreshStamp } = useMeiliRefreshState();
  const { resetChat } = useRag();

  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const isMountedRef = useRef(true);

  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleLogout = async (event?: React.FormEvent) => {
    if (event) event.preventDefault();
    setLoading(true);
    try {
      // 1st, Ensure server-side sign out properly
      await signOut();
      // 2nd, Reset Zustand state
      resetSelectedSituations(); // Reset selected situations in Zustand (to avoid stale state for new user login)
      resetSelectedThoughts(); // Reset selected thoughts in Zustand (to avoid stale state for new user login)
      resetSelectedSuggestedThoughts(); // Reset selected suggested thoughts in Zustand (to avoid stale state for new user login)
      resetSelectedOrigins(); // Reset selected origins in Zustand (to avoid stale state for new user login)
      // 3rd, Increment component refresh keys to force re-fetching Meili searchInstances
      incrementMeiliRefreshStamp("backlog_origins"); // Refresh backlog origins component (Meili searchInstance cache)
      incrementMeiliRefreshStamp("studio_suggested_thoughts"); //  Refresh studio suggested thoughts component (Meili searchInstance cache)
      incrementMeiliRefreshStamp("studio_thoughts"); //  Refresh studio thoughts component (Meili searchInstance cache)
      // 4th, Reset RAG chat state
      resetChat(); // Reset RAG chat state in Zustand

      // Only call logout if component is still mounted
      if (isMountedRef.current) {
        logout(); // Clear auth state in Zustand
      }

      // Force a hard navigation to clear all client state
      if (isMountedRef.current) {
        router.replace(authSettings.redirects.login);
      }
    } catch {
      if (isMountedRef.current) {
        router.replace(authSettings.redirects.login);
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  return { loading, handleLogout };
}
